/**
 * Site 3D: any coordinate in 3D - live satellite imagery month by month,
 * terrain and building outlines - and, at an audited site, the dug pits
 * carved into the ground with the month each part was dug.
 *
 * Height of the ground comes only from the survey at an audited site; the
 * public terrain elsewhere is SRTM/Copernicus (about 30 m, a few metres of
 * error). Buildings are satellite-traced outlines with an estimated height.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';
import {
  ATTR, ESRI_URL, MONTHS, OVERTURE_BUILDINGS, YEAR, demSetFor, demSource, demTiles, ensureProtocols, groundHeight, loadSite, loadSiteIndex,
  metresBetween, registerSiteDem, sentinelMonth, setTerrainCarver, type MonthImagery, type Site, type SiteIndexEntry,
} from '../map/site3d';
import {
  KIND_DEFAULTS, buildWaterModel, capacity, ensureWaterProtocol, setWaterPicture, stored, waterCarver, waterTiles, type WaterBody, type WaterModel,
} from '../map/water3d';
import { apiUrl } from '../api/client';

const DUG_COLOURS = ['#f5d06f', '#f0a04b', '#d9622b', '#a3341c', '#6e1d12'] as const; // Jun..Oct
const empty = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });
const area = (m2: number): string => (m2 < 10000 ? `${fmt(m2)} m²` : `${(m2 / 10000).toFixed(1)} ha`);
const SHORT: Record<WaterBody['kind'], string> = {
  'Rain-harvesting / farm pond': 'Farm pond', 'Pond / tank': 'Tank', 'Lake / reservoir': 'Reservoir', 'Stream / canal': 'Stream',
};
/** Bodies that get a basin and a volume (streams are only drawn). */
const modelled = (m: WaterModel | null): WaterBody[] => (m ? m.bodies.filter((b) => b.kind !== 'Stream / canal') : []);

function parseCoords(s: string): [number, number] | null {
  const m = s.trim().match(/^\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (!m) return null;
  const lat = Number(m[1]), lon = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? [lat, lon] : null;
}

const fmt = (n: number): string => Math.round(n).toLocaleString('en-IN');

export function Site3D({ query }: { query: URLSearchParams }): JSX.Element {
  const holder = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);
  const [ready, setReady] = useState(false);
  const qLat = Number(query.get('lat')), qLon = Number(query.get('lon'));
  const start: [number, number] = Number.isFinite(qLat) && Number.isFinite(qLon) && query.get('lat') ? [qLat, qLon] : [24.5614, 74.8165];
  const [coordText, setCoordText] = useState(`${start[0]}, ${start[1]}`);
  const [target, setTarget] = useState<[number, number]>(start);
  const [coordError, setCoordError] = useState('');
  const [month, setMonth] = useState(Math.min(9, Math.max(0, Number(query.get('m') ?? 10) - 1)));
  const [imagery, setImagery] = useState<'s2' | 'esri'>('s2');
  const [s2, setS2] = useState<{ state: 'loading' | 'ready' | 'none'; info?: MonthImagery }>({ state: 'loading' });
  const [exag, setExag] = useState(3);
  const exagRef = useRef(exag);
  const [showBuildings, setShowBuildings] = useState(true);
  const [showDepth, setShowDepth] = useState(true);
  const [showDug, setShowDug] = useState(true);
  const [sites, setSites] = useState<SiteIndexEntry[]>([]);
  const [site, setSite] = useState<Site | null>(null);
  const [showWater, setShowWater] = useState(true);
  const [water, setWater] = useState<WaterModel | null>(null);
  const [waterNote, setWaterNote] = useState('');
  const [pick, setPick] = useState<number | null>(null);
  const [carveVer, setCarveVer] = useState(0);
  const waterMarkers = useRef<maplibregl.Marker[]>([]);

  useEffect(() => { loadSiteIndex().then(setSites).catch(() => setSites([])); }, []);

  // local ground height at the chosen point; terrain is drawn relative to it
  const [base, setBase] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    groundHeight(target[0], target[1]).then((h) => live && setBase(Math.floor(h) - 2)).catch(() => live && setBase(0));
    return () => { live = false; };
  }, [target]);
  const demKey = useRef('');

  // ------------------------------------------------------------ map
  useEffect(() => {
    if (!holder.current || mapRef.current) return;
    ensureProtocols();
    const map = new maplibregl.Map({
      container: holder.current,
      center: [start[1], start[0]], zoom: 16.2, pitch: 62, bearing: -28, maxPitch: 80,
      attributionControl: { compact: true },
      style: {
        version: 8,
        sources: {
          esri: { type: 'raster', tiles: [ESRI_URL], tileSize: 256, maxzoom: 19, attribution: ATTR.esri },
          bld: { type: 'vector', url: `pmtiles://${OVERTURE_BUILDINGS}`, attribution: ATTR.buildings },
          dug: { type: 'geojson', data: empty() },
          wall: { type: 'geojson', data: empty() },
        },
        layers: [
          { id: 'bg', type: 'background', paint: { 'background-color': '#0d1117' } },
          { id: 'esri', type: 'raster', source: 'esri' },
          { id: 'dug', type: 'fill', source: 'dug', paint: { 'fill-color': ['match', ['get', 'month'], 6, DUG_COLOURS[0], 7, DUG_COLOURS[1], 8, DUG_COLOURS[2], 9, DUG_COLOURS[3], DUG_COLOURS[4]], 'fill-opacity': 0.55, 'fill-outline-color': 'rgba(0,0,0,0.35)' } },
          { id: 'wall', type: 'line', source: 'wall', paint: { 'line-color': '#ffffff', 'line-width': 2, 'line-dasharray': [2, 1.2] } },
          {
            id: 'bld', type: 'fill-extrusion', source: 'bld', 'source-layer': 'building', minzoom: 13,
            paint: {
              'fill-extrusion-color': '#d9dfdb',
              'fill-extrusion-height': ['coalesce', ['get', 'height'], ['*', ['coalesce', ['get', 'num_floors'], 0], 3.2], 4],
              'fill-extrusion-opacity': 0.88,
            },
          },
        ],
      },
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    map.on('error', (e) => console.warn('[site3d]', (e as { error?: Error }).error?.message ?? e));
    if (import.meta.env.DEV) (window as unknown as { __s3d?: MapLibreMap }).__s3d = map;
    map.on('load', () => setReady(true));
    return () => { setReady(false); map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------ terrain: the carved set for the month at a site
  // Declared before the fly-to on purpose. A flight started with no terrain throws on every frame
  // once terrain appears mid-flight (MapLibre 4.7 reads an elevation start it never recorded),
  // which jams the render queue for good. So terrain goes on first and the flight waits for it.
  const demSet = site ? demSetFor(month + 1) : 'base';
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || base === null) return;
    // Replace the source rather than setTiles(): setTiles while the first tiles are
    // still loading aborts them and MapLibre throws the AbortError out of the call.
    const tiles = demTiles(site?.id ?? null, demSet);
    const key = `${tiles[0]}|${base}`;
    if (demKey.current === key) return;
    demKey.current = key;
    map.setTerrain(null);
    if (map.getSource('dem')) map.removeSource('dem');
    map.addSource('dem', demSource(tiles, base));
    map.setTerrain({ source: 'dem', exaggeration: exagRef.current });
  }, [demSet, site, ready, base, carveVer]);

  // ------------------------------------------------------------ ponds, tanks, reservoirs around the point
  useEffect(() => {
    let live = true;
    setWater(null);
    setPick(null);
    if (!showWater) { setWaterNote(''); return; }
    setWaterNote('Finding ponds, tanks and reservoirs within 2 km…');
    buildWaterModel(target[0], target[1], (note) => { if (live) setWaterNote(note); })
      .then((m) => { if (live) { setWater(m); setWaterNote(''); } })
      .catch(() => { if (live) setWaterNote('Could not read the water record just now; try again in a minute.'); });
    return () => { live = false; };
  }, [target, showWater]);

  // their basins cut into the terrain (and again whenever a depth is changed)
  useEffect(() => { setCarveVer(setTerrainCarver(water ? waterCarver(water) : null)); }, [water]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (map.getLayer('water')) map.removeLayer('water');
    if (map.getSource('water')) map.removeSource('water');
    setWaterPicture(water ? { model: water, month, pick } : null);
    if (!water) return;
    ensureWaterProtocol();
    map.addSource('water', { type: 'raster', tiles: waterTiles(), tileSize: 256, bounds: water.bbox, minzoom: 10, maxzoom: 16 });
    map.addLayer({ id: 'water', type: 'raster', source: 'water', paint: { 'raster-resampling': 'nearest', 'raster-fade-duration': 0 } }, 'dug');
  }, [water, month, pick, ready]);

  useEffect(() => {
    const map = mapRef.current;
    waterMarkers.current.forEach((m) => m.remove());
    waterMarkers.current = [];
    if (!map || !ready || !water) return;
    // the larger ones get a label on the map; every body is in the side list
    modelled(water).slice(0, 15).forEach((b, i) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `s3d-water-tag${pick === b.id ? ' is-on' : ''}`;
      el.textContent = `${i + 1} · ${SHORT[b.kind]}`;
      el.addEventListener('click', (e) => { e.stopPropagation(); setPick(b.id); });
      waterMarkers.current.push(new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat(b.center).addTo(map));
    });
  }, [water, pick, ready]);

  const pickBody = (b: WaterBody): void => {
    setPick(b.id);
    mapRef.current?.flyTo({ center: b.center, zoom: b.areaMax > 50000 ? 16.4 : 17.4, pitch: 62, duration: 1400, freezeElevation: true });
  };
  const setDepth = (id: number, depth: number): void => {
    if (!Number.isFinite(depth) || depth <= 0 || depth > 40) return;
    setWater((w) => (w ? { ...w, bodies: w.bodies.map((b) => (b.id === id ? { ...b, depth } : b)) } : w));
  };

  // ------------------------------------------------------------ go to a coordinate, pick up an audited site there
  const hasGround = base !== null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !hasGround) return;
    const near = sites.find((s) => metresBetween([s.center[0], s.center[1]], [target[1], target[0]]) < 2000);
    // an audited site is framed close enough to read the pits; anywhere else, the wider area
    map.flyTo({ center: near ? near.center : [target[1], target[0]], zoom: near ? 17.4 : 16.2, pitch: 62, bearing: -28, duration: 1800, freezeElevation: true });
    if (!near) { setSite(null); return; }
    if (site?.id === near.id) return;
    loadSite(near.id).then((s) => {
      if (!s) return;
      registerSiteDem(s.id, s.dem_sets);
      setSite(s);
    }).catch(() => setSite(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, ready, sites, hasGround]);

  // ------------------------------------------------------------ site overlays
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    markers.current.forEach((m) => m.remove());
    markers.current = [];
    if (map.getLayer('depth')) map.removeLayer('depth');
    if (map.getSource('depth')) map.removeSource('depth');
    if (!site) {
      (map.getSource('dug') as maplibregl.GeoJSONSource).setData(empty());
      (map.getSource('wall') as maplibregl.GeoJSONSource).setData(empty());
      return;
    }
    map.addSource('depth', { type: 'image', url: apiUrl(`/data/sites/${site.id}/${site.depth_image.url}`), coordinates: site.depth_image.coordinates as [[number, number], [number, number], [number, number], [number, number]] });
    map.addLayer({ id: 'depth', type: 'raster', source: 'depth', paint: { 'raster-opacity': 0.9, 'raster-resampling': 'nearest' } }, 'wall');
    fetch(apiUrl(`/data/sites/${site.id}/dug.geojson`)).then((r) => r.json()).then((fc: GeoJSON.FeatureCollection) => {
      (map.getSource('dug') as maplibregl.GeoJSONSource | undefined)?.setData(fc);
    }).catch(() => undefined);
    (map.getSource('wall') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features: site.wall.length ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: site.wall } }] : [] });
    site.pits.forEach((p, i) => {
      const el = document.createElement('div');
      el.className = 's3d-pit';
      el.textContent = `Pit ${i + 1} · ${p.max_m.toFixed(1)} m`;
      markers.current.push(new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat([p.lon, p.lat]).addTo(map));
    });
  }, [site, ready]);

  // ------------------------------------------------------------ month: dug cells, depth, pit labels
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const n = month + 1;
    if (map.getLayer('dug')) map.setFilter('dug', ['<=', ['get', 'month'], n]);
    if (map.getLayer('depth')) map.setLayoutProperty('depth', 'visibility', showDepth && n >= 9 ? 'visible' : 'none');
    map.setLayoutProperty('dug', 'visibility', showDug && !(showDepth && n >= 9) ? 'visible' : 'none');
    markers.current.forEach((m) => { m.getElement().style.display = n >= 7 ? '' : 'none'; });
  }, [month, site, ready, showDepth, showDug]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    let live = true;
    const setLayer = (tiles: string | null): void => {
      if (map.getLayer('s2')) map.removeLayer('s2');
      if (map.getSource('s2')) map.removeSource('s2');
      if (tiles) {
        map.addSource('s2', { type: 'raster', tiles: [tiles], tileSize: 256, maxzoom: 16, attribution: ATTR.s2 });
        map.addLayer({ id: 's2', type: 'raster', source: 's2' }, map.getLayer('water') ? 'water' : 'dug');
      }
    };
    if (imagery === 'esri') {
      setLayer(null);
      map.setLayoutProperty('esri', 'visibility', 'visible');
      return () => { live = false; };
    }
    // the sharp image stays underneath while the month's scene is found, and when there is none
    map.setLayoutProperty('esri', 'visibility', 'visible');
    setS2({ state: 'loading' });
    sentinelMonth(target[0], target[1], month).then((info) => {
      if (!live) return;
      if (!info) { setLayer(null); setS2({ state: 'none' }); return; }
      setLayer(info.tiles);
      map.setLayoutProperty('esri', 'visibility', 'none');
      setS2({ state: 'ready', info });
    });
    return () => { live = false; };
  }, [imagery, month, target, ready]);

  useEffect(() => {
    exagRef.current = exag;
    if (mapRef.current && ready && mapRef.current.getSource('dem')) mapRef.current.setTerrain({ source: 'dem', exaggeration: exag });
  }, [exag, ready]);
  useEffect(() => { if (mapRef.current && ready) mapRef.current.setLayoutProperty('bld', 'visibility', showBuildings ? 'visible' : 'none'); }, [showBuildings, ready]);

  const go = (e: FormEvent): void => {
    e.preventDefault();
    const c = parseCoords(coordText);
    if (!c) { setCoordError('Enter latitude and longitude, for example 24.5614, 74.8165'); return; }
    setCoordError('');
    setTarget(c);
    window.history.replaceState(null, '', `#/site3d?lat=${c[0]}&lon=${c[1]}&m=${month + 1}`);
  };

  const bodies = modelled(water);
  const picked = bodies.find((b) => b.id === pick) ?? null;
  const pickedNo = picked ? bodies.indexOf(picked) + 1 : 0;
  const totals = useMemo(() => {
    let cap = 0, held = 0, seenCap = 0, unseen = 0;
    for (const b of modelled(water)) {
      const c = capacity(b), s = stored(b, month);
      cap += c;
      if (s === null) unseen++; else { held += s; seenCap += c; }
    }
    return { cap, held, pct: seenCap ? Math.round((100 * held) / seenCap) : null, unseen };
  }, [water, month]);
  const pct = (b: WaterBody, m: number): string => {
    const s = stored(b, m), c = capacity(b);
    return s === null ? '—' : `${c ? Math.round((100 * s) / c) : 0}%`;
  };

  const row = site?.months[month];
  const msl = (rl: number | null): string => (rl === null || !site ? '' : ` (${(rl + site.rl_to_msl).toFixed(1)} m)`);
  const s2Label = useMemo(() => {
    if (imagery === 'esri') return 'Sharp image: Esri World Imagery (28 Mar 2025 capture here, before the digging).';
    if (s2.state === 'loading') return `Finding the clearest Sentinel-2 scene in ${MONTHS[month]} ${YEAR}…`;
    if (s2.state === 'none') return `No Sentinel-2 scene under 40% cloud in ${MONTHS[month]} ${YEAR}; showing the sharp image instead.`;
    const i = s2.info!;
    return `Sentinel-2, clearest scene ${new Date(i.sceneDate + 'T00:00:00Z').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} (${i.cloud}% cloud over the scene), 10 m pixels.`;
  }, [imagery, s2, month]);

  return (
    <div className="farms-shell">
      <aside className="farms-side s3d-side">
        <header className="farms-side__head">
          <h1>Site 3D</h1>
          <p>Live satellite imagery, terrain and buildings in 3D for any coordinate, with every pond, tank and reservoir around it modelled month by month. An audited site adds its dug pits and the month each part was dug.</p>
        </header>

        <form className="s3d-go" onSubmit={go}>
          <label htmlFor="s3d-coords">Coordinates (latitude, longitude)</label>
          <div className="s3d-go__row">
            <input id="s3d-coords" value={coordText} onChange={(e) => setCoordText(e.target.value)} placeholder="24.5614, 74.8165" inputMode="decimal" />
            <button type="submit" className="btn-primary">Go</button>
          </div>
          {coordError ? <p className="s3d-error">{coordError}</p> : null}
          {sites.length ? (
            <div className="chips">
              {sites.map((s) => (
                <button key={s.id} type="button" className={`chip-toggle${site?.id === s.id ? ' is-on' : ''}`}
                  onClick={() => { setCoordText(`${s.center[1]}, ${s.center[0]}`); setTarget([s.center[1], s.center[0]]); }}>
                  {s.name}
                </button>
              ))}
            </div>
          ) : null}
        </form>

        <section className="s3d-block">
          <h2>Month, {YEAR}</h2>
          <div className="s3d-months" role="radiogroup" aria-label="Month">
            {MONTHS.map((m, i) => (
              <button key={m} type="button" role="radio" aria-checked={month === i} className={`s3d-month${month === i ? ' is-on' : ''}`} onClick={() => setMonth(i)}>{m}</button>
            ))}
          </div>
          <div className="chips">
            <button type="button" className={`chip-toggle${imagery === 's2' ? ' is-on' : ''}`} onClick={() => setImagery('s2')}>Sentinel-2 of the month</button>
            <button type="button" className={`chip-toggle${imagery === 'esri' ? ' is-on' : ''}`} onClick={() => setImagery('esri')}>Sharp image</button>
          </div>
          <p className="s3d-note">{s2Label}</p>
        </section>

        <section className="s3d-block">
          <h2>Layers</h2>
          <label className="s3d-check"><input type="checkbox" checked={showBuildings} onChange={(e) => setShowBuildings(e.target.checked)} /> 3D buildings (estimated height)</label>
          {site ? <label className="s3d-check"><input type="checkbox" checked={showDepth} onChange={(e) => setShowDepth(e.target.checked)} /> Depth still open (Sep–Oct)</label> : null}
          {site ? <label className="s3d-check"><input type="checkbox" checked={showDug} onChange={(e) => setShowDug(e.target.checked)} /> Dug ground, by month</label> : null}
          <label className="s3d-range" htmlFor="s3d-exag">
            <span>Terrain height ×{exag}</span>
            <input id="s3d-exag" type="range" min={1} max={8} step={1} value={exag} onChange={(e) => setExag(Number(e.target.value))} />
          </label>
        </section>

        <section className="s3d-block s3d-water">
          <h2>Ponds, tanks &amp; reservoirs</h2>
          <label className="s3d-check"><input type="checkbox" checked={showWater} onChange={(e) => setShowWater(e.target.checked)} /> Model every water body within 2 km, in 3D</label>
          {waterNote ? <p className="s3d-note">{waterNote}</p> : null}
          {water ? (
            <>
              <p className="s3d-water__sum">
                {bodies.length ? `${bodies.length} found: ` : 'No pond, tank or reservoir found within 2 km.'}
                {(['Lake / reservoir', 'Pond / tank', 'Rain-harvesting / farm pond'] as const)
                  .map((k) => [k, bodies.filter((b) => b.kind === k).length] as const).filter(([, n]) => n)
                  .map(([k, n]) => `${n} ${k.toLowerCase()}${n > 1 ? 's' : ''}`).join(', ')}
                {water.bodies.length > bodies.length ? ` (and ${water.bodies.length - bodies.length} stream or canal stretch, drawn only)` : ''}
              </p>
              {bodies.length ? (
                <div className="s3d-figs">
                  <div><span>Full capacity, all</span><strong>≈ {fmt(totals.cap)} m³</strong><em>at the assumed depths</em></div>
                  <div><span>Holding in {MONTHS[month]}</span>
                    {totals.pct === null ? <><strong>—</strong><em>no clear image this month</em></>
                      : <><strong>≈ {fmt(totals.held)} m³</strong><em>{totals.pct}% of what was seen{totals.unseen ? `; ${totals.unseen} not seen` : ''}</em></>}
                  </div>
                </div>
              ) : null}
              <ol className="s3d-water__list">
                {bodies.slice(0, 25).map((b, i) => (
                  <li key={b.id}>
                    <button type="button" className={pick === b.id ? 'is-on' : ''} onClick={() => pickBody(b)}>
                      <b>{i + 1}</b><span>{SHORT[b.kind]} · {area(b.areaMax)}</span><span>{fmt(capacity(b))} m³</span><span>{MONTHS[month]} {pct(b, month)}</span>
                    </button>
                  </li>
                ))}
              </ol>
              {bodies.length > 25 ? <p className="s3d-note">and {bodies.length - 25} smaller ones (pale blue on the map).</p> : null}
              {picked ? (
                <div className="s3d-water__card">
                  <h3>{pickedNo} · {picked.kind}</h3>
                  <dl className="s3d-month-card">
                    <dt>Full outline</dt><dd>{area(picked.areaMax)}</dd>
                    <dt>Long record</dt><dd>{picked.occurrence ? `under water ${picked.occurrence}% of the time at its deepest, 1984–2021` : 'not in the 1984–2021 record (newer, or under 30 m)'}</dd>
                    <dt>Full depth</dt>
                    <dd>
                      <input className="s3d-depth" type="number" min={0.5} max={40} step={0.5} value={picked.depth} aria-label="Full depth in metres"
                        onChange={(e) => setDepth(picked.id, Number(e.target.value))} /> m{' '}
                      <span className="muted">{picked.depth === KIND_DEFAULTS[picked.kind].depth ? 'assumed; enter the real depth if known' : 'entered'}</span>
                    </dd>
                    <dt>Capacity</dt><dd>≈ {fmt(capacity(picked))} m³</dd>
                  </dl>
                  <table className="s3d-water__table">
                    <thead><tr><th>{YEAR}</th><th>Rain</th><th>Water spread</th><th>Holding</th></tr></thead>
                    <tbody>
                      {MONTHS.map((m, i) => {
                        const s = stored(picked, i), a = picked.areas[i];
                        return (
                          <tr key={m} className={i === month ? 'is-on' : ''} onClick={() => setMonth(i)}>
                            <td>{m}</td>
                            <td>{water.rain[i] == null ? '—' : `${Math.round(water.rain[i] as number)} mm`}</td>
                            <td>{a == null ? (water.scenes[i] ? 'cloud' : 'no image') : a ? area(a) : 'dry'}</td>
                            <td>{s == null ? '—' : `${fmt(s)} m³ (${pct(picked, i)})`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : bodies.length ? <p className="s3d-note">Pick one in the list or on the map for its month-by-month record.</p> : null}
              <p className="s3d-note">
                Outlines and the monthly water spread are measured: JRC Global Surface Water (1984–2021, 30 m) and the clearest Sentinel-2 scene of each month (10 m). Basins are cut into the terrain, deepest where water stood most often. Kinds go by size (under 0.25 ha, under 5 ha, larger), so a quarry trench of that size is listed as a farm pond. Nobody has surveyed their depth: the full depth is assumed (farm ponds and tanks 3 m, reservoirs 6 m) and every volume scales with it. Floating weed or deep shadow can hide water in a month. Rain: Open-Meteo ERA5.
              </p>
            </>
          ) : null}
        </section>

        {site ? (
          <section className="s3d-block s3d-site">
            <h2>{site.name}</h2>
            <div className="s3d-figs">
              <div><span>Still open after refill</span><strong>≈ {fmt(site.figures.open_whole_m3)} m³</strong><em>{fmt(site.figures.open_whole_range_m3[0])}–{fmt(site.figures.open_whole_range_m3[1])}</em></div>
              <div><span>Refilled by vendor</span><strong>≤ {fmt(site.figures.refill_max_m3[1])} m³</strong><em>best bound {fmt(site.figures.refill_max_m3[0])}–{fmt(site.figures.refill_max_m3[1])}</em></div>
              <div><span>Dug in all</span><strong>≈ {fmt((site.figures.dug_total_m3[0] + site.figures.dug_total_m3[1]) / 2)} m³</strong><em>{fmt(site.figures.dug_total_m3[0])}–{fmt(site.figures.dug_total_m3[1])}</em></div>
              <div><span>Deepest pit</span><strong>{site.pits[0]?.max_m.toFixed(1)} m</strong><em>{fmt(site.pits[0]?.area_m2 ?? 0)} m², avg {site.pits[0]?.mean_m.toFixed(1)} m</em></div>
            </div>
            {row ? (
              <dl className="s3d-month-card">
                <dt>{row.m} {YEAR}</dt><dd>{row.state}</dd>
                <dt>Plot level</dt><dd>{row.level === null ? 'not surveyed' : `RL ${row.level.toFixed(2)}${msl(row.level)}`}</dd>
                <dt>Pit 1 floor</dt><dd>{row.pit === null ? 'not surveyed' : `RL ${row.pit.toFixed(2)}${msl(row.pit)}`}</dd>
                <dt>Water</dt><dd>{row.water ? 'None' : 'Seen'} · {row.s2} clear Sentinel-2, {row.dswx} clear NASA water maps</dd>
              </dl>
            ) : null}
            <div className="s3d-legend">
              <span className="s3d-legend__ramp" aria-hidden="true" /><span className="muted">Depth still open: 0 → 3.5 m</span>
              <ul className="map-legend">
                {['Jun', 'Jul', 'Aug', 'Sep', 'Oct'].map((m, i) => <li key={m}><i style={{ background: DUG_COLOURS[i] }} />Dug by {m}</li>)}
              </ul>
            </div>
            <p className="s3d-note">{site.notes.depth} {site.notes.timeline} {site.notes.water}</p>
          </section>
        ) : (
          <section className="s3d-block">
            <p className="s3d-note">No audited site within 2 km of this point. Imagery, terrain and buildings are live; pits and the monthly record appear for audited sites{sites.length ? `: ${sites.map((s) => s.name).join(', ')}` : ''}.</p>
          </section>
        )}
        <p className="s3d-note">Buildings are outlines traced from satellite images (Google, Microsoft via Overture Maps); no lidar exists for this area, so every building is drawn at an estimated 4 m. Terrain outside audited sites is SRTM/Copernicus, about 30 m and a few metres of error.</p>
      </aside>
      <div ref={holder} className="farms-map" />
    </div>
  );
}
